import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Check, X } from "lucide-react";

// Each notification has a friendly explanation and a default subject placeholder.
const NOTIF_META = [
  { key: "welcome",           label: "Bienvenue",                  desc: "Email de bienvenue à l'inscription d'un·e nouvel·le élève." },
  { key: "group_approved",    label: "Groupe local validé",        desc: "Au membre qui a soumis un groupe local, quand admin l'approuve." },
  { key: "group_pending",     label: "Groupe local en attente",    desc: "Aux administrateurs, lorsqu'un·e membre soumet un nouveau groupe." },
  { key: "action_joined",     label: "Confirmation participation", desc: "Confirmation envoyée quand un·e élève clique « Participer » à une action." },
  { key: "badge_unlocked",    label: "Badge débloqué",             desc: "Quand un·e élève gagne un nouveau badge sur son profil." },
  { key: "consultation_new",  label: "Nouvelle consultation",      desc: "Quand une consultation est publiée — peut spammer si activé. Désactivé par défaut." },
  { key: "newsletter",        label: "Newsletter mensuelle",        desc: "Envoi groupé manuel. Désactivé par défaut, à activer si vous voulez l'envoyer." },
  { key: "action_reminder",   label: "Rappel J-3 avant action",    desc: "DÉSACTIVÉ — vous avez demandé à ne PAS envoyer d'email avant une action. Garder OFF." },
];

export default function EmailConfigTab() {
  const [cfg, setCfg] = React.useState(null);

  React.useEffect(() => {
    api.get("/site-config").then((r) => setCfg(r.data?.emails || {}));
  }, []);

  if (!cfg) return <div>Chargement…</div>;

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setNotif = (key, patch) => setCfg((c) => ({
    ...c,
    notifications: { ...(c.notifications || {}), [key]: { ...((c.notifications || {})[key] || {}), ...patch } },
  }));

  const save = async () => {
    try {
      await api.patch("/admin/site-config", { emails: cfg });
      toast.success("Paramètres email enregistrés");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="grid gap-6" data-testid="email-config-tab">
      <section className="neo-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h2 className="font-display font-black text-2xl tracking-tighter uppercase flex items-center gap-2"><Mail className="w-6 h-6" /> Paramètres email (Brevo)</h2>
          <label className="inline-flex items-center gap-2 chip bg-cef-yellow cursor-pointer" data-testid="emails-master-switch">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setField("enabled", e.target.checked)} />
            <strong>{cfg.enabled ? "ENVOI ACTIVÉ" : "ENVOI DÉSACTIVÉ"}</strong>
          </label>
        </div>
        <p className="text-sm text-neutral-600">Aucun email ne sera envoyé tant que ce <strong>switch principal</strong> n'est pas activé. Chaque type d'email a aussi sa propre case à cocher individuelle ci-dessous.</p>
      </section>

      <section className="neo-card p-5 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2 font-display font-black text-lg uppercase tracking-tight">Expéditeur</div>
        <input className="neo-input" placeholder="Nom expéditeur" value={cfg.sender_name || ""} onChange={(e) => setField("sender_name", e.target.value)} data-testid="email-sender-name" />
        <input className="neo-input" placeholder="Email expéditeur" value={cfg.sender_email || ""} onChange={(e) => setField("sender_email", e.target.value)} data-testid="email-sender-email" />
        <input className="neo-input" placeholder="Reply-to" value={cfg.reply_to || ""} onChange={(e) => setField("reply_to", e.target.value)} data-testid="email-reply-to" />
        <input className="neo-input" placeholder="Signature de bas de page" value={cfg.footer_signature || ""} onChange={(e) => setField("footer_signature", e.target.value)} data-testid="email-footer" />
      </section>

      <section className="neo-card p-5">
        <div className="font-display font-black text-lg uppercase tracking-tight mb-3">Notifications par type</div>
        <div className="grid gap-3" data-testid="email-notifications-list">
          {NOTIF_META.map(({ key, label, desc }) => {
            const n = (cfg.notifications || {})[key] || {};
            const enabled = !!n.enabled;
            return (
              <div key={key} className={`p-3 border-2 rounded-md ${enabled ? "border-cef-green bg-cef-green/10" : "border-black/20 bg-white"}`} data-testid={`email-notif-${key}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2">
                      <strong>{label}</strong>
                      {enabled ? <span className="chip bg-cef-green text-[10px] py-0"><Check className="w-3 h-3 inline" /> ACTIF</span> : <span className="chip bg-white text-[10px] py-0 text-neutral-500">OFF</span>}
                    </div>
                    <p className="text-xs text-neutral-600 mt-1">{desc}</p>
                    <input
                      className="neo-input mt-2 text-sm"
                      placeholder="Sujet de l'email"
                      value={n.subject || ""}
                      onChange={(e) => setNotif(key, { subject: e.target.value })}
                      data-testid={`email-notif-${key}-subject`}
                    />
                  </div>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={enabled} onChange={(e) => setNotif(key, { enabled: e.target.checked })} data-testid={`email-notif-${key}-toggle`} />
                    <span className="text-xs font-bold">{enabled ? "ON" : "OFF"}</span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <button onClick={save} className="neo-btn bg-cef-pink text-white max-w-xs" data-testid="email-config-save">Enregistrer les paramètres email</button>
    </div>
  );
}
